import Handlebars from '../shared/handlebar-helpers';

import {
    getLangFile,
    getQueryParams,
    getRedirectUrl,
    onOsDownloadQuote,
    onOsProductPage,
    recalculateCart,
} from '../shared/shared';

import '../../styles/app.scss';
import Notification from '../shared/notification';

import { getHook } from './hooks';
import { getConnectionDetails } from './lib/connectionDetails';
import { Modal } from '../shared/modal';
import redirectHndb from './templates/redirect.handlebars';
import ApiService from '../shared/services';
import Session from '../shared/session';
import { SimpleMessage } from '../shared/simple-message';

const EVENTS = {
    OC_READY: 'omnicustomer:ready',
    CART_REDIRECT: 'omnicustomer:cartredirect',
    HANDLE_CONNECT: 'omnicustomer:handle'
}

export const STORAGE_ACTIONS = {
    CONNECTION_DETAILS: 'oc_connection_details',
}

async function start() {
    if (window.os_started) return;
    console.log('### Omni customer started');
    window.oc_started = true;

    const userLang = navigator.language;
    const lang = userLang.substring(0, 2);
    const translationFile = await getLangFile(lang);
    Handlebars.translation = await getLangFile(lang);

    const app = {
        service: new ApiService('customer', null, BCSuite),
        session: new Session('customerSession'),
    };

    const {id: currentCartId, version: currentCartVersion} = await app.service.getCartDetails() ?? {id: null, version: null};
    let {quotedCartId, quotedCartVersion} = app.session.sessionDetails ?? {};

    if (quotedCartId == currentCartId) {  
        if (quotedCartVersion == -1) {
            app.service.postWebhook('cart', 'loaded', {
                "scope": "store/cart/loaded",
                "data": {
                  "type": "cart",
                  "id": currentCartId,
                  "version": currentCartVersion,
                },
                "hash": currentCartId, 
                "created_at": Date.now(),
                "producer": "Omni Customer"
            });
            app.session.set({
                sessionDetails: {
                    quotedCartVersion: currentCartVersion,
                }
            });
            quotedCartVersion = currentCartVersion;
        }
    } else {
        app.session.remove();
    }

        
    if (!currentCartId) {
        app.session.sessionDetails = app.session.getBaseSession().sessionDetails;
    } else {
        app.session.set({
            sessionDetails: {
                currentCartId,
                currentCartVersion,
            }
        });

        const tracker = BCSuite.getCartTracker();
    
        tracker.onCartUpdate(async (args) => {
            const { cart } = args;
            app.session.set({
                sessionDetails: {
                    currentCartId: cart.id,
                    currentCartVersion: cart.version,
                }
            });

            recalculateCart(app, quotedCartId, quotedCartVersion);
        });
    
        if (quotedCartId == currentCartId && quotedCartVersion < currentCartVersion) { 
            recalculateCart(app, quotedCartId, currentCartVersion);
        }
    }

    /* Run the default hooks, enabled based on the store config */
    const hooks = getHook();
    hooks.onCartPage(app);
    hooks.onCartPopup(app);

    const connectMessage = 'Mensaje de contacto {{tcc}}';
    const connectQuery = "a[href^='https://api.whatsapp.com/send']";
    const connectPhone = window.scfConfig?.phoneNumber ?? 1234;
    const queryParams = getQueryParams();
    
    onOsDownloadQuote((quoteId, buildUrl) => {
        new SimpleMessage({title: Handlebars.translation.requested_download.requested_quote, 
            body: Handlebars.translation.requested_download.request_ready, 
            c2a: Handlebars.translation.requested_download.view_quote, 
            c2a_link: buildUrl(app.service.getBaseUrl())
        })
        .open();
    });

    onOsProductPage((value)=> {
        const [tcc, productId, variantId] = value.split('-');
        
        const data = {
            "scope": "store/product/viewed",
            "data": {
              "type": "product",
              "id": productId,
              "variant_id": variantId,
              "tcc": tcc,
            },
            "hash": `${productId}-${variantId}-${tcc}`,
            "created_at": Date.now(),
            "producer": "Omni Customer"
        };

        app.service.postWebhook('product', 'viewed', data);
    });

    if (queryParams['os_tcc']) {
        const notif = new Notification({type: "loading", message: 'Loading tcc'});
        const data = {
            tcc: queryParams['os_tcc'],
            redirectTo: '',
            success: false,
            redirect: true,
            message: '',
            button: ''
        };
        notif.open();
        const res = await app.service.getQuotedCartDisclaimer(data.tcc);
        
        if (res.status !== 0) {
            data.success = false;
            window.dispatchEvent(new CustomEvent(EVENTS.CART_REDIRECT, {detail: data}));
            return notif.reload({type: 'error', message: res.message});
        }
        
        notif.close();
        data.message = res.data.message;
        data.button = res.data.button;

        const redirectModal = new Modal({padding: '24px', width: '80%', maxWidth: '400px', backgroundColor: '#EEEEEE'});
        redirectModal.update(redirectHndb({
            message: data.message,
            button: data.button,
            optin: res.data.optin
        }));

        let accepted = res.data.optin?.default;

        redirectModal.modalContainer.querySelector('#oc-tcc-redirect-optin')?.addEventListener('click', (e) => {
            if (e.target.checked) {
                accepted = true;
            } else {
                accepted = false;
            }
        });

        redirectModal.modalContainer.querySelector('#oc-tcc-redirect')?.addEventListener('click', async(e) => {
            e.preventDefault();
            const {data: loaderTcc, status, message} = await app.service.getQuotedCartLoaderByTcc(data.tcc, {accepted});
            data.redirectTo = getRedirectUrl(loaderTcc.redirect_url);
            data.success = true;
            window.dispatchEvent(new CustomEvent(EVENTS.CART_REDIRECT, {detail: data}));

            if (res.status !== 0) {
                data.success = false;
                window.dispatchEvent(new CustomEvent(EVENTS.CART_REDIRECT, {detail: data}));
                return notif.reload({type: 'error', message: res.message});
            }

            app.session.set({
                sessionDetails: {
                    quotedCartId: loaderTcc.cart_id,
                    quotedCartVersion: -1,
                }
            });
            window.location.href = data.redirectTo;
        });
        redirectModal.open();

    }

    const redirectWithMessage = (text = null, params = {}) => {
        let redirect = `https://api.whatsapp.com/send?phone=${connectPhone}`;
    
        const queryParams = new URLSearchParams(params);
    
        if (text) {
            queryParams.append('text', text);
        }
    
        const queryString = queryParams.toString();
    
        if (queryString) {
            redirect += `&${queryString}`;
        }
    
        return (window.location.href = redirect);
    };


    /* Handles clicking on connect  */
    const handleConnect = async (e) => {
        e.preventDefault();
        const msg = {
            value: connectMessage,
            redirect: true,
            tcc: ''
        };
        const connection = await getConnectionDetails(app.service);

        if (connection) {
            msg.tcc = connection.tcc;
            msg.value = msg.value.replace('{{tcc}}', msg.tcc);
        }

        window.dispatchEvent(new CustomEvent(EVENTS.HANDLE_CONNECT, {detail: msg}));

        if (msg.redirect) {
            connection && connection.online
            ? redirectWithMessage(msg.value, { os_online: 1 })
            : redirectWithMessage(msg.value);
        }
    };

    /* Allow customization on customers */
    const omniCustomer = {
        api: {
            getTcc: app.servicegetCartTcc,
            handleConnect,
        },
        values: {
            connectPhone,
            connectMessage,
            connectQuery,
        },
        hooks: {
            onHandleConnect: (fn) => window.addEventListener(EVENTS.HANDLE_CONNECT, fn),
            onCartRedirect: (fn) => window.addEventListener(EVENTS.CART_REDIRECT, fn),
        },
        ready: true,
    };

    window.omniCustomer = omniCustomer;

    window.dispatchEvent(new CustomEvent(EVENTS.OC_READY, {
        detail: {
            omniCustomer
        }
    }));
}

start();
